import type { ObservedSpeaker, TranscriptEntry } from "../../shared/types.ts";

export function observedSpeakersFromEntries(entries: TranscriptEntry[]): ObservedSpeaker[] {
  const speakersByKey = new Map<string, ObservedSpeaker>();
  for (const entry of entries) {
    const speakerKey = entry.speakerKey?.trim();
    if (!speakerKey || speakersByKey.has(speakerKey)) continue;
    const provisionalSpeaker = entry.rawSpeaker?.trim() || entry.speaker;
    const displayName = entry.speaker !== provisionalSpeaker ? entry.speaker : null;
    speakersByKey.set(speakerKey, {
      speakerKey,
      provisionalSpeaker,
      displayName,
      capture: entry.capture ?? null,
    });
  }
  return Array.from(speakersByKey.values()).sort(compareObservedSpeakers);
}

export function projectTranscriptSpeakerNames(
  entries: TranscriptEntry[],
  speakers: ObservedSpeaker[],
): TranscriptEntry[] {
  const speakersByKey = new Map(speakers.map((speaker) => [speaker.speakerKey, speaker]));
  return entries.map((entry) => {
    const speakerKey = entry.speakerKey?.trim();
    if (!speakerKey) return entry;
    const observed = speakersByKey.get(speakerKey);
    const speaker = observed?.displayName?.trim() || observed?.provisionalSpeaker.trim();
    return speaker && speaker !== entry.speaker ? { ...entry, speaker } : entry;
  });
}

export function compareObservedSpeakers(a: ObservedSpeaker, b: ObservedSpeaker): number {
  const aRun = a.capture?.captureRunIndex;
  const bRun = b.capture?.captureRunIndex;
  if (aRun != null && bRun != null && aRun !== bRun) return aRun - bRun;
  if (aRun != null && bRun == null) return -1;
  if (aRun == null && bRun != null) return 1;

  const aNumber = a.capture?.speakerNumber;
  const bNumber = b.capture?.speakerNumber;
  if (aNumber != null && bNumber != null && aNumber !== bNumber) return aNumber - bNumber;

  return a.provisionalSpeaker.localeCompare(b.provisionalSpeaker);
}
